/**
 * The runtime environment an app resolves at startup. Owned here (not by any
 * consumer): a plain, framework-agnostic concept that downstream helpers — e.g.
 * Unleash's `createUnleashConfig` — merely *consume*.
 */
export type Env = 'dev' | 'test' | 'prod' | 'offline';

export interface GetEnvOptions {
  /** Whether the app is running against the local Vite dev server. */
  isDevEnv: boolean;
  /** Whether the app is running in the production environment. */
  isProdEnv: boolean;
  /**
   * Whether the app is running with no backend and no Keycloak, i.e. a local
   * E2E build. Takes precedence over the other flags. Defaults to `false`, so
   * apps that never run offline can leave it out.
   */
  isOfflineEnv?: boolean;
}

/**
 * Factory for an app's `getEnv` resolver: maps the app-specific environment
 * flags to the shared {@link Env}.
 *
 * The mapping (`offline → 'offline'`, `dev → 'dev'`, `prod → 'prod'`, otherwise
 * `'test'`) is shared across every app; only the flag *detection* differs (each
 * app inspects its own `window.env.ENVIRONMENT` strings), so apps supply the
 * resolved booleans.
 *
 * `offline` is checked first: an offline build is served from localhost and is
 * neither `dev` nor a deployed environment, so it must not fall through to
 * `test`. Consumers treat it like `dev` (no monitoring SDK, no Keycloak).
 *
 * @example
 * ```ts
 * export const getEnv = createGetEnv({ isDevEnv, isProdEnv, isOfflineEnv });
 * ```
 */
export const createGetEnv =
  ({ isDevEnv, isProdEnv, isOfflineEnv = false }: GetEnvOptions) =>
  (): Env => {
    if (isOfflineEnv) {
      return 'offline';
    }
    if (isDevEnv) {
      return 'dev';
    }
    if (isProdEnv) {
      return 'prod';
    }
    return 'test';
  };
